/**
 * Scribe User Memory hub — settings sub-page at /settings/memory.
 * Available to every authenticated user (App.tsx ProtectedRoute gates auth);
 * whether memory affects summaries is the per-user `user_memory_enabled`
 * toggle rendered below, which is on until the clinician turns it off.
 */

import { Header, Sidebar } from '@/components/layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { useSettings } from '@/hooks/useSettings';
import { Loader2 } from 'lucide-react';
import { useMemoryEntries } from '../useUserMemoryQueries';
import { ActiveMemoryList } from './ActiveMemoryList';
import { CreateMemoryForm } from './CreateMemoryForm';
import { SuggestedMemoryList } from './SuggestedMemoryList';

export const MemoryPage = () => {
  const { userMemoryEnabled, setUserMemoryEnabled, isLoading: settingsLoading } = useSettings();
  // Fetch retired alongside suggested/active so the on/off toggle can turn a
  // rule back on — the BFF never returns `candidate` rows to the SPA.
  const { data: entries, isLoading, isError } = useMemoryEntries('suggested,active,retired');

  const suggestedEntries = (entries ?? []).filter((entry) => entry.status === 'suggested');
  const activeEntries = (entries ?? []).filter((entry) => entry.status === 'active' || entry.status === 'retired');
  const hasNoEntries = suggestedEntries.length === 0 && activeEntries.length === 0;

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col">
        <Header title="Minni" />
        <main className="flex-1 overflow-hidden bg-slate-50/30">
          <ScrollArea className="h-full">
            <div className="mx-auto max-w-4xl space-y-6 p-8">
              <div>
                <h2 className="font-bold text-2xl text-slate-900">Minni</h2>
                <p className="mt-1 text-muted-foreground text-sm">
                  Skríban lærir af breytingum þínum á samantektum og býr til leiðbeiningar um stíl, uppbyggingu, orðalag
                  og fagorð fyrir næstu samantektir — aldrei um klínískt innihald.
                </p>
              </div>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Minni virkt</CardTitle>
                    <Switch
                      id="user-memory-enabled"
                      checked={userMemoryEnabled}
                      onCheckedChange={setUserMemoryEnabled}
                      disabled={settingsLoading}
                      aria-label="Minni virkt"
                    />
                  </div>
                  <CardDescription>
                    Þegar kveikt er á minni eru virkar reglur notaðar til að sérsníða framtíðarsamantektir. Slökktu til
                    að hætta tímabundið að nota leiðbeiningarnar. Skríban heldur áfram að greina breytingar og safna
                    tillögum, en þær eru ekki notaðar á meðan slökkt er.
                  </CardDescription>
                </CardHeader>
              </Card>

              {isLoading && (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}

              {isError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700 text-sm">
                  Ekki tókst að sækja minni. Vinsamlegast reyndu aftur síðar.
                </div>
              )}

              {!isLoading && !isError && (
                <>
                  {hasNoEntries && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg">Engar tillögur ennþá</CardTitle>
                        <CardDescription>
                          Skríban greinir reglulega breytingar þínar á samantektum og býr til tillögur sjálfkrafa.
                        </CardDescription>
                      </CardHeader>
                    </Card>
                  )}

                  {suggestedEntries.length > 0 && (
                    <Card>
                      <CardContent className="space-y-4 pt-6">
                        <Label className="text-muted-foreground text-sm">Nýjar tillögur úr greiningu</Label>
                        <SuggestedMemoryList entries={suggestedEntries} />
                      </CardContent>
                    </Card>
                  )}

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Virkt minni</CardTitle>
                      <CardDescription>
                        Reglur sem eru í notkun núna, hvort sem Skríban lærði þær af breytingum þínum eða þú bjóst þær
                        til. Slökktu á reglu til að hætta að nota hana.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ActiveMemoryList entries={activeEntries} />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Búa til minni handvirkt</CardTitle>
                      <CardDescription>
                        Skrifaðu leiðbeiningu beint í stað þess að bíða eftir að Skríban læri hana.
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <CreateMemoryForm />
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
          </ScrollArea>
        </main>
      </div>
    </div>
  );
};

export default MemoryPage;
